import BackButtonToolbar from "features/BackButtonToolbar";
import React from "react";
import {
  ArrayField,
  Button,
  Datagrid,
  DateField,
  FunctionField,
  Labeled,
  NumberField,
  Show,
  SimpleShowLayout,
  TextField,
  useNotify,
} from "react-admin";
import { SectionTitle } from "features/SectionTitle";
import { Box, Grid, Typography, useMediaQuery } from "@mui/material";
import { settings } from "app";
import { ScholarshipStatusesChoices, SchorashipTypeChoices } from "utils/choices";
import { Currency, ScholarshipStatuses } from "utils/constants";
import permissions from "app/permissions";
import SecuredComponent from "server/security/SecuredComponent";
import DisqualifyDialog from "./DisqualifyDialog";
import ApproveDialog from "./ApproveDialog";
import { getFile } from "server/components/DownloadFile";
import VisibilityIcon from "@mui/icons-material/Visibility";
import UserField from "server/components/Fields/UserField";
import ReturnForCorrectionDialog from "./ReturnForCorrectionDialog";

const ShowScholarshipApplications = () => {
  const isMobile = useMediaQuery(theme => theme.breakpoints.down("md"));
  const notify = useNotify();

  return (
    <Show
      title="Детайли на кандидатура за стипендии"
      actions={<BackButtonToolbar />}
    >
      <SimpleShowLayout>
        <SectionTitle
          title="Информация"
          textAlign="center"
          paddingTop="2rem"
        />
        <Grid
          container
          spacing={3}
          sx={isMobile ? settings.style.shadowBoxMobile : settings.style.shadowBox}
        >
          <Grid
            item
            xs={12}
            md={4}
          >
            <Labeled label="Име на кампания">
              <TextField source="campaignName" />
            </Labeled>
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
          >
            <Labeled label="Име">
              <FunctionField
                render={rec =>
                  rec.studentSecondName
                    ? `${rec.studentFirstName} ${rec.studentSecondName} ${rec.studentLastName}`
                    : `${rec.studentFirstName} ${rec.studentLastName}`
                }
              />
            </Labeled>
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
          >
            <Labeled label="Факултетен  номер">
              <TextField source="facultyNumber" />
            </Labeled>
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
          >
            <Labeled label="Специалност">
              <TextField source="specialityName" />
            </Labeled>
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
          >
            <Labeled label="Тип стипендия">
              <FunctionField
                render={rec => SchorashipTypeChoices.find(option => option.id === rec.scholarshipType)?.name}
              />
            </Labeled>
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
          >
            <Labeled label="Оценка">
              <TextField
                source="grade"
                emptyText="-"
              />
            </Labeled>
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
          >
            <Labeled label="Бал">
              <TextField
                source="rank"
                emptyText="-"
              />
            </Labeled>
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
          >
            <Labeled label="Стипендия">
              <NumberField
                source="income"
                options={{ style: "currency", currency: Currency.EUR }}
                emptyText="-"
              />
            </Labeled>
          </Grid>

          <Grid
            item
            xs={12}
            md={4}
          >
            <Labeled label="Дата на създаване">
              <DateField
                source="createdOn"
                locales="bg-BG"
                showTime
              />
            </Labeled>
          </Grid>

          <Grid
            item
            xs={12}
            md={4}
          >
            <Labeled label="Документи за доход">
              <FunctionField
                render={rec =>
                  rec?.incomeDocumentIds?.length > 0 ? (
                    <>
                      {rec.incomeDocumentIds?.map((id, idx) => (
                        <Box
                          key={id}
                          sx={{ mb: 1 }}
                        >
                          <Button
                            onClick={() => getFile(id, notify)}
                            label={`Покажи документ ${idx + 1}`}
                            startIcon={<VisibilityIcon />}
                            variant="contained"
                            fullWidth
                          />
                        </Box>
                      ))}
                    </>
                  ) : (
                    <Typography>Няма качени документи</Typography>
                  )
                }
              />
            </Labeled>
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
          >
            <Labeled label="Статус на кандидатура">
              <FunctionField
                render={rec => ScholarshipStatusesChoices.find(option => option.id === rec.status)?.name}
              />
            </Labeled>
          </Grid>

          <Grid
            item
            xs={12}
            sx={{ display: "flex", alignItems: "flex-end" }}
          >
            <FunctionField
              render={rec =>
                [ScholarshipStatuses.Pending, ScholarshipStatuses.PendingAccounting].includes(rec?.status) && (
                  <Box sx={{ display: "flex", gap: 2 }}>
                    <SecuredComponent permission={permissions.canCreateScholarshipBlacklistStudents}>
                      <DisqualifyDialog studentEducationId={rec?.studentEducationId} />
                    </SecuredComponent>
                    <SecuredComponent permission={permissions.canReturnScholarshipApplicationsForCorrection}>
                      <ReturnForCorrectionDialog id={rec?.id} />
                    </SecuredComponent>
                    <SecuredComponent
                      permission={[
                        permissions.canApproveScholarshipApplications,
                        permissions.canApprovePendingScholarshipApplications,
                        permissions.canApprovePendingAccountingScholarshipApplications,
                      ]}
                    >
                      <ApproveDialog id={rec?.id} />
                    </SecuredComponent>
                  </Box>
                )
              }
            />
          </Grid>
        </Grid>

        <SectionTitle
          paddingY={2}
          title="История на статусите"
          textAlign="center"
          paddingTop="2rem"
        />
        <Grid
          container
          sx={isMobile ? settings.style.shadowBoxMobile : settings.style.shadowBox}
        >
          <Grid
            item
            xs={12}
          >
            <ArrayField source="statusHistory">
              <Datagrid
                rowClick={false}
                bulkActionButtons={false}
                sx={settings.style.secondaryDatagrid}
                empty={<Typography>Няма история на статусите</Typography>}
              >
                <FunctionField
                  label="Статус"
                  render={rec => ScholarshipStatusesChoices.find(option => option.id === rec.status)?.name ?? "-"}
                />
                <TextField
                  source="statusComment"
                  label="Коментар"
                  emptyText="-"
                />
                <UserField
                  source="statusChangedByUserId"
                  label="Променен от"
                />
                <DateField
                  source="statusChangedOn"
                  label="Променен на"
                  locales="bg-BG"
                  showTime
                  emptyText="-"
                />
              </Datagrid>
            </ArrayField>
          </Grid>
        </Grid>
      </SimpleShowLayout>
    </Show>
  );
};

export default ShowScholarshipApplications;
